import { useAtom, useSetAtom } from 'jotai';
import { Tag } from '@carbon/react';
import {
  SpatialFilterControls as MapFilterControls,
  type SpatialFilterMode,
} from 'components/query-map';
import { aoiEditingAtom, spatialFilterAtom } from './atoms';
import type { SpatialMode } from './types';
import styles from './spatial-filter-controls.module.scss';

/** The Global Map Filter modes, in display order (mock §4b). */
const SPATIAL_MODES: SpatialFilterMode<SpatialMode>[] = [
  { mode: 'packageArea', label: 'Package Area' },
  { mode: 'extent', label: 'Use Visible Map Extent' },
  { mode: 'aoi', label: 'Draw AOI Polygon' },
];

/**
 * Global Map Filter mode control — the shared map filter radios over the three
 * spatial modes (Package Area / Use Visible Map Extent / Draw AOI Polygon), bound to
 * `spatialFilterAtom`; the reducer clears any captured polygon on a mode change.
 *
 * In AOI mode the polygon is drawn directly on the map (no toolbar icons — see
 * `DrawAoiControl`) and managed from here: once a polygon exists, an applied-area card
 * shows below the radios with **Edit** (enters vertex editing on the map via the
 * controlled `aoiEditingAtom`) and **Delete** (clear the polygon → the map re-arms a
 * fresh draw). The card is intentionally STATIC across editing — Edit just initiates map
 * editing; the panel doesn't reshuffle. `aoiEditingAtom` is a two-way mirror of the map's
 * edit mode (it clears itself when the user clicks away, so a later Edit re-triggers), and
 * the polygon stays the single source of truth in `spatialFilterAtom`.
 *
 * @return {JSX.Element} The spatial-mode radio group + the applied-AOI card.
 */
export function SpatialFilterControls() {
  const [{ mode, polygon }, dispatch] = useAtom(spatialFilterAtom);
  const setEditing = useSetAtom(aoiEditingAtom);

  const deleteAoi = () => {
    dispatch({ type: 'setPolygon', polygon: null });
    setEditing(false);
  };

  return (
    <div className={styles.controls}>
      <MapFilterControls
        name="eval3-spatial-mode"
        idPrefix="eval3-spatial"
        legendText="Map filter mode"
        modes={SPATIAL_MODES}
        mode={mode}
        onModeChange={(next) => dispatch({ type: 'setMode', mode: next })}
        aoi={
          mode === 'aoi' && polygon
            ? {
                hasPolygon: true,
                editLabel: 'Edit area',
                deleteLabel: 'Delete area',
                onEdit: () => setEditing(true),
                onDelete: deleteAoi,
                children: (
                  <Tag type="blue" size="sm">
                    Polygon drawn
                  </Tag>
                ),
              }
            : undefined
        }
      />
    </div>
  );
}
